import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Kleine, durch die mobile Adressleiste ausgeloeste Resizes ignorieren -
// verhindert Scroll-Jank, ohne echte Layout-Aenderungen (Orientation) zu blockieren.
ScrollTrigger.config({ ignoreMobileResize: true });

export class BaseWebsite {
  constructor() {
    this.isNavigating = false;
    this.init();
  }

  init() {
    // Immediately mark that JavaScript has loaded to show content
    document.body.classList.add('js-loaded');

    this.setupNavigation();
    this.setupScrollAnimations();
    this.setupInteractions();
    // Auto-Alignment (Snap-to-Section nach Scroll-Stopp) deaktiviert:
    // verursachte Wobble nach Navigation und ist fragwuerdiges UX.
    // this.setupSectionAlignment();

    // Hide loading state after setup
    setTimeout(() => {
      document.body.classList.add('loaded');
    }, 100);

    // Trigger-Positionen neu berechnen, wenn sich das Layout wirklich aendert:
    // nach vollem Laden (Bilder/Fonts verschieben Positionen) und bei
    // Orientation-Wechsel (Delay, bis der Viewport nach dem Dreh stabil ist).
    window.addEventListener('load', () => ScrollTrigger.refresh());
    window.addEventListener('orientationchange', () => {
      setTimeout(() => ScrollTrigger.refresh(), 300);
    });
  }

  // Collapsing/Reveal-Hero (nur Desktop): Der Hero wird gepinnt, die Folge-
  // Section scrollt per hoeherem z-index darueber - aber nur ueber die Leerzone
  // unter dem Button (Pin-Dauer = Leerzone). Danach loest der Pin und alles
  // scrollt gemeinsam weiter, sodass der Hero-Content nie ueberdeckt wird.
  setupHeroPinReveal(heroSelector, nextSelector) {
    const hero = document.querySelector(heroSelector);
    if (!hero) return;

    // Scroll-Maus (ALLE Viewports, nicht nur Desktop): erst NACH der Hero-
    // Einblendung erscheinen lassen, danach beim Runterscrollen ausblenden
    // (direkt aus scrollY, bewusst OHNE ScrollTrigger, da der vom Pin gestoert wird).
    const mouse = hero.querySelector('.scroll-mouse');
    if (mouse) {
      gsap.set(mouse, { opacity: 0 });
      const scrollFade = () => {
        mouse.style.opacity = String(Math.max(0, 1 - window.scrollY / 140));
      };
      gsap.delayedCall(2.5, () => {
        if (window.scrollY < 20) {
          gsap.fromTo(mouse,
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.6,
              ease: 'power2.out',
              onComplete: () => window.addEventListener('scroll', scrollFade, { passive: true })
            }
          );
        } else {
          window.addEventListener('scroll', scrollFade, { passive: true });
          scrollFade();
        }
      });
    }

    // Hero-CTA beim Runterscrollen sanft ausblenden (ergaenzt das Abdecken durch
    // die Folge-Section; verhindert auch ein Durchblitzen bei der Parallax).
    const heroBtn = hero.querySelector('.hero-button');
    if (heroBtn) {
      const btnFade = () => {
        const o = Math.max(0, 1 - window.scrollY / 180);
        heroBtn.style.opacity = String(o);
        heroBtn.style.pointerEvents = o < 0.1 ? 'none' : '';
      };
      window.addEventListener('scroll', btnFade, { passive: true });
    }

    // Ab hier: Pin-Reveal nur Desktop
    if (window.innerWidth < 769) return;

    const next = document.querySelector(nextSelector);
    const lastContent = hero.querySelector('.hero-button') || hero.querySelector('.hero-content');
    if (!next || !lastContent) return;

    // Leerzone unter dem Button, Layout-basiert (offsetTop/offsetHeight
    // ignorieren die Einblend-Transforms -> schon beim Erstellen korrekt).
    const emptyBelow = () => {
      const btn = hero.querySelector('.hero-button');
      if (!btn) return 0;
      let top = 0, el = btn;
      while (el && el !== hero) { top += el.offsetTop; el = el.offsetParent; }
      // Reveal bis zur (statischen) Button-Position: die Folge-Section rastet an
      // der Button-Linie ein, nicht darueber. KEIN Extra-Puffer mehr - der zog die
      // Intro (mit ihrem Naht-Divider) zu weit hoch, sodass der Divider auf
      // Subtitle-Hoehe klebte. Der Content-Parallax hebt Subtitle/Button beim
      // Scrollen ohnehin an -> es bleibt ein sauberer Gap ueber dem Divider.
      return Math.max(0, Math.round(hero.offsetHeight - top));
    };

    // Pin ERST nach vollem Laden erstellen (Fonts/Layout final) mit FESTEM
    // end-Wert - ein Funktions-end wurde von ScrollTrigger zu frueh/lazy
    // ausgewertet und lieferte eine viel zu kurze Distanz.
    const buildPin = () => {
      const distance = emptyBelow();

      // Schutz: ohne echte Leerzone weder Pin noch z-Staffelung. Ein Pin mit
      // Distanz ~0 naehme den Hero bei pinSpacing:false dauerhaft aus dem
      // Layoutfluss, und die darueber gelegte Section verdeckte ihn komplett.
      if (distance < 40) return;

      // Folge-Section ueber den Hero legen (erst jetzt, mit gesicherter Leerzone)
      hero.style.zIndex = '0';
      next.style.position = 'relative';
      next.style.zIndex = '2';

      ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: '+=' + distance,
        pin: hero,
        pinSpacing: false,
        anticipatePin: 1
      });
    };
    if (document.readyState === 'complete') buildPin();
    else window.addEventListener('load', buildPin);
  }

  setupIntroAnimation() {
    const introEls = gsap.utils.toArray('.intro-lead, .intro-text');
    if (introEls.length) {
      // Beim Laden abspielen (nicht scroll-getriggert), damit der Inhalt
      // immer sichtbar ist - auch wenn man per CTA/Nav direkt hierher springt
      // und der ScrollTrigger sonst nicht zuverlaessig feuert. Hebt zugleich den
      // FOUC-Guard (.intro-lead/.intro-text { opacity: 0 }) wieder auf.
      gsap.fromTo(introEls,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          stagger: 0.15,
          delay: 0.2
        }
      );
    }
  }

  setupNavigation() {
    const nav = document.getElementById('navigation');
    let navVisible = true;
    
    // Mobile menu functionality
    const mobileToggle = document.querySelector('.mobile-menu-toggle');
    const navLinks = document.querySelector('.nav-links');
    
    if (mobileToggle && navLinks) {
      const setMenuState = (open) => {
        mobileToggle.classList.toggle('active', open);
        navLinks.classList.toggle('active', open);
        mobileToggle.setAttribute('aria-expanded', String(open));
      };

      mobileToggle.addEventListener('click', () => {
        setMenuState(!mobileToggle.classList.contains('active'));
      });

      // Close menu when clicking on a link
      navLinks.addEventListener('click', (e) => {
        if (e.target.tagName === 'A') {
          setMenuState(false);
        }
      });

      // Close menu when clicking outside
      document.addEventListener('click', (e) => {
        if (!nav.contains(e.target)) {
          setMenuState(false);
        }
      });
    }

    // Smooth scroll for navigation links and buttons with data-scroll-to
    const scrollToHandler = (e, targetSelector) => {
      e.preventDefault();
      this.isNavigating = true;

      // Menue sichtbar halten - der Nav-Offset am Ziel rechnet mit sichtbarem Menue
      gsap.to(nav, { y: 0, duration: 0.3, overwrite: true });
      navVisible = true;

      const target = document.querySelector(targetSelector);
      if (target) {
        // Feste Layout-Position minus Hoehe der fixierten Navigation,
        // damit die Section mit Abstand unter dem Menue landet.
        const navOffset = (nav ? nav.offsetHeight : 80) + 24;
        const targetY = Math.max(0, target.offsetTop - navOffset);
        gsap.to(window, {
          duration: 1,
          scrollTo: { y: targetY },
          ease: "power2.inOut",
          onComplete: () => {
            // isNavigating kurz true halten, damit die Auto-Hide-Logik
            // (Scroll/Mousemove) das Menue waehrend und direkt nach dem
            // Nav-Scroll nicht ausblendet.
            setTimeout(() => {
              this.isNavigating = false;
            }, 1500);
          }
        });
      }
    };

    // Logo-Klicks scrollen sanft zum Seitenanfang: das Nav-Logo nur auf der
    // Startseite (auf Unterseiten bleibt es der Link nach Hause), das
    // Footer-Logo auf jeder Seite.
    const scrollToTop = (e) => {
      e.preventDefault();
      this.isNavigating = true;
      gsap.to(nav, { y: 0, duration: 0.3, overwrite: true });
      navVisible = true;
      gsap.to(window, {
        duration: 1,
        scrollTo: { y: 0 },
        ease: "power2.inOut",
        onComplete: () => {
          setTimeout(() => { this.isNavigating = false; }, 500);
        }
      });
    };

    const isHome = location.pathname === '/' || location.pathname === '/index.html';
    const navLogo = document.querySelector('.logo-main');
    if (navLogo && isHome) {
      navLogo.addEventListener('click', scrollToTop);
    }

    const footerLogo = document.querySelector('.contact-logo');
    if (footerLogo) {
      footerLogo.style.cursor = 'pointer';
      footerLogo.addEventListener('click', scrollToTop);
    }

    // Handle anchor links with href="#..."
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        scrollToHandler(e, anchor.getAttribute('href'));
      });
    });

    // Handle elements with data-scroll-to attribute
    document.querySelectorAll('[data-scroll-to]').forEach(element => {
      element.addEventListener('click', (e) => {
        const targetId = element.getAttribute('data-scroll-to');
        scrollToHandler(e, `#${targetId}`);
      });
    });

    // Navigation background on scroll
    let lastScrollTop = 0;

    // Nav sichtbar halten, solange die Oberkante der Intro-Section (nur
    // Index-Seite) noch im Viewport ist - sobald sie oben rausscrollt,
    // greift das normale Auto-Hide.
    const isIntroInView = () => {
      const intro = document.getElementById('intro');
      return !!intro && intro.getBoundingClientRect().top > 0;
    };

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > 50) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }

      // Solange die Intro-Section im Viewport ist: Nav sichtbar halten
      if (isIntroInView()) {
        if (!navVisible && !this.isNavigating) {
          gsap.to(nav, {
            y: 0,
            duration: 0.8,
            ease: "back.out(1.4)"
          });
          navVisible = true;
        }
      // Hide navigation when scrolling down past 100px (nicht waehrend Nav-Scroll)
      } else if (!this.isNavigating && scrollTop > lastScrollTop && scrollTop > 100) {
        // Scrolling down and not near top
        if (navVisible) {
          gsap.to(nav, {
            y: -100,
            duration: 0.6,
            ease: "back.in(1.7)"
          });
          navVisible = false;
        }
      } else if (scrollTop <= 50) {
        // Always show when near the top of page
        if (!navVisible) {
          gsap.to(nav, {
            y: 0,
            duration: 0.8,
            ease: "back.out(1.4)"
          });
          navVisible = true;
        }
      }

      lastScrollTop = scrollTop;
    }, { passive: true });

    // Show/hide navigation based on mouse position
    window.addEventListener('mousemove', (e) => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (e.clientY <= 100 && !navVisible && scrollTop > 100) {
        // Mouse near top of viewport and we're scrolled down - show nav
        gsap.to(nav, {
          y: 0,
          duration: 0.7,
          ease: "elastic.out(1, 0.8)"
        });
        navVisible = true;
      } else if (!this.isNavigating && !isIntroInView() && e.clientY > 100 && navVisible && scrollTop > 100) {
        // Mouse left top area and we're scrolled down - hide nav
        gsap.to(nav, {
          y: -100,
          duration: 0.5,
          ease: "back.in(1.2)"
        });
        navVisible = false;
      }
    }, { passive: true });

    // Beim Laden mit Hash (Cross-Page-Link "/#intro" von einer Unterseite):
    // Der Ziel-Offset unter der fixierten Navi kommt aus CSS (scroll-padding-top).
    // Hier nur das Menue sichtbar halten, damit das Auto-Hide es nicht ausblendet.
    if (window.location.hash && window.location.hash.length > 1) {
      this.isNavigating = true;
      gsap.set(nav, { y: 0 });
      navVisible = true;
      clearTimeout(this._hashNavTimeout);
      this._hashNavTimeout = setTimeout(() => {
        this.isNavigating = false;
      }, 1500);
    }
  }

  setupScrollAnimations() {
    // Scroll-Reveals: EIN Trigger pro Sektion mit Block-Stagger statt einzelner
    // Trigger je h1/h2/h3/p - untere Elemente erreichten ihren Trigger sonst
    // frueher als obere und blendeten VOR ihnen ein (unruhiger Seitenrhythmus).
    // Der fruehere Section-Fade (ganze Section per opacity) entfiel ebenfalls:
    // Hintergruende sind einfach da (flimmerte an den Hell-Dunkel-Naehten).
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    const revealGroup = (targets, trigger, stagger = 0.07) => {
      if (!targets.length) return;
      gsap.fromTo(targets,
        { y: 14, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.55, ease: "power3.out", stagger,
          scrollTrigger: {
            trigger,
            start: isMobile ? "top 94%" : "top 86%",
            once: true
          }
        }
      );
    };

    if (!reduce) {
      // 1. Ein Reveal pro Sektion: die Container-Bloecke staggern top-down.
      gsap.utils.toArray('section, footer.contact-section').forEach(section => {
        if (section.id === 'hero' || section.classList.contains('elixir-hero')) return;
        if (section.classList.contains('intro-section')) return;   // laeuft load-basiert (setupIntroAnimation)

        const container = section.querySelector('.container');
        if (!container) return;

        const blocks = Array.from(container.children);
        revealGroup(blocks, section);

        // Item-Gruppen (Prozess-Schritte, Benefits, Tech-Items, Galerie,
        // Logo-Raster) mit eigenem Trigger, damit sie nicht mit dem
        // Section-Header zusammen laufen.
        blocks.forEach(b => {
          // Device-Cluster NICHT als Item-Gruppe staggern: GSAP liesse sonst
          // Inline-Transforms auf den .device-Elementen zurueck, die deren
          // CSS-Transforms (Tilt, Hover, Ausweichen) dauerhaft ueberstimmen.
          // Der Cluster revealt bereits als ganzer Block.
          if (b.classList.contains('device-cluster')) return;
          const kids = Array.from(b.children);
          if (kids.length >= 2 && kids.every(k => k.querySelector('h3, h4, img'))) {
            revealGroup(kids, b, kids.length > 6 ? 0.04 : 0.09);
          }
        });
      });

      // (Der frueher gesonderte Projekt-Intro-Stagger entfiel mit der Section.)
    }

    // Section lines animation
    gsap.utils.toArray('.section-line').forEach(line => {
      gsap.fromTo(line, {
        width: 0,
        opacity: 0
      }, {
        width: '135px',
        opacity: 1,
        duration: 1.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: line,
          start: "top bottom",
          toggleActions: "play none none none"
        }
      });
    });

    // (Section-Header-Reveal entfaellt: der Header ist ein Container-Block und
    // laeuft im Sektions-Stagger oben mit - ein zweiter Tween wuerde kaempfen.)

    // Parallax-Bloecke komplett nur ohne Reduced-Motion-Praeferenz.
    if (reduce) return;

    // Background parallax for sections
    // WICHTIG: Die Heroes (.hero-section/.elixir-hero) sind hier ABSICHTLICH
    // ausgenommen - sie werden per setupHeroPinReveal gepinnt. Eine zusaetzliche
    // yPercent-Parallax kaempft mit dem Pin -> wandernde Hero-Unterkante (graue
    // Naht) + Flackern beim Hochscrollen (scrub-Lag). Nicht wieder aufnehmen.
    gsap.utils.toArray('.elixir-expertise-section, .intro-section').forEach(section => {
      gsap.to(section, {
        yPercent: -10,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: 3
        }
      });
    });

    // Hero-Parallax: bewusst NUR auf dem Content-Block (Text + Button), nicht auf
    // der gepinnten Sektion. Die alte Section-Parallax hat zwei Dinge gemacht, die
    // erwuenscht waren - Content leicht anheben + beim Reveal einen Gap zwischen
    // aufsteigender Folge-Section und Button lassen (der Button wandert beim
    // Scrollen mit hoch) - aber dafuer die Sektion-Unterkante bewegt -> graue Naht
    // + Flackern. Auf dem Content-Block ist beides unkritisch: Sektion-Unterkante
    // bleibt fix, und der Pin misst die Leerzone per offsetTop (ignoriert den
    // Transform) -> Pin-Distanz bleibt stabil.
    gsap.utils.toArray('.hero-section .hero-content').forEach(content => {
      const heroSection = content.closest('.hero-section');
      gsap.to(content, {
        yPercent: -15,
        ease: "none",
        scrollTrigger: {
          trigger: heroSection,
          start: "top bottom",
          end: "bottom top",
          scrub: 5
        }
      });
    });

    // Zwei-Ebenen-Parallax, konsistent ueber alle Content-Sektionen.
    // 1. Text-Ebene: der Container jeder Sektion driftet leicht und traege -
    //    nichts steht "festgenagelt" neben driftenden Bildern. Heroes sind
    //    ausgenommen (Pin), die Intro driftet bereits als ganze Section.
    gsap.utils.toArray('section, footer.contact-section').forEach(section => {
      if (section.id === 'hero' || section.classList.contains('elixir-hero')) return;
      if (section.classList.contains('intro-section')) return;
      const container = section.querySelector('.container');
      if (!container) return;
      // Drift symmetrisch um die Sektionsmitte (+2.5 -> -2.5, nicht 0 -> -5):
      // sonst ist der Inhalt an der Unterkante schon hochgewandert und das
      // Section-Padding wirkt unten groesser als oben.
      gsap.fromTo(container, { yPercent: 2.5 }, {
        yPercent: -2.5,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: 2.5
        }
      });
    });

    // 2. Bild-Ebene: Mocks, Ninja-Posen, Elixir-Flaschen/Glaeser, Tech-Logos,
    //    Dataviz-Bento, Team-Grafik und Testimonial-Card laufen mit eigenem
    //    Versatz VOR der Text-Ebene her (additiv zur Container-Drift).
    gsap.utils.toArray(
      '.device-showcase .showcase-item, #smartgrid .device-cluster, ' +
      '.ninja-img, .meditation-img, .benefit-image, .tech-image, ' +
      '.dataviz-bento, .team-image img, .testimonial-media'
    ).forEach(el => {
      // Wie die Text-Ebene symmetrisch um die Mitte (+4 -> -4 statt 0 -> -8),
      // damit Mocks am Sektionsende nicht dauerhaft "zu hoch" stehen.
      gsap.fromTo(el, { yPercent: 4 }, {
        yPercent: -4,
        ease: "none",
        force3D: true,   // eigene GPU-Ebene -> beim Scrollen nur komponieren,
                         // nicht die (teuren) Device-Schatten neu zeichnen
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: 2.5
        }
      });
    });

    // 3. Zoom-Entrance der Mocks: beim Reinscrollen von leicht verkleinert auf
    //    volle Groesse (scrub-gebunden, fertig sobald der Mock die Viewport-
    //    Mitte erreicht). Liegt auf denselben Wrappern wie die Bild-Ebene -
    //    GSAP komponiert scale + yPercent, die CSS-Transforms der .device-
    //    Kinder (Tilt/Hover) bleiben unberuehrt.
    gsap.utils.toArray('.device-showcase .showcase-item, #smartgrid .device-cluster, .dataviz-bento').forEach(el => {
      gsap.fromTo(el,
        { scale: 0.8 },
        {
          scale: 1,
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top bottom",
            end: "top 45%",
            scrub: 1.2
          }
        }
      );
    });

    // 4. Aufrichtung des SmartGrid-Clusters: alle drei Screens kommen geneigt
    //    herein (Monitor rueckgekippt, iPad/MacBook seitlich eingedreht) und
    //    drehen sich beim Reinscrollen in die frontale Endposition - der
    //    Hover-Look als finaler Default. BEWUSST auf den .device-screens
    //    (nicht den .devices): so bleiben deren CSS-Hover (Lift/Scale,
    //    Ausweichen) frei von GSAP-Inline-Transforms.
    [
      { sel: '#smartgrid .device-display .device-screen', from: { rotationX: 16, transformOrigin: '50% 100%' } },
      { sel: '#smartgrid .device-ipad .device-screen',    from: { rotationY: -14, transformOrigin: '50% 50%' } },
      { sel: '#smartgrid .device-macbook .device-screen', from: { rotationY: 14, transformOrigin: '50% 50%' } }
    ].forEach(({ sel, from }) => {
      gsap.utils.toArray(sel).forEach(el => {
        gsap.fromTo(el, from,
          {
            rotationX: 0,
            rotationY: 0,
            ease: "none",
            scrollTrigger: {
              trigger: el.closest('.device-cluster'),
              start: "top bottom",
              end: "top 45%",
              scrub: 1.2
            }
          }
        );
      });
    });

    // (Logo-Parallax entfernt: yPercent -3 mit scrub 4 lag unter der
    // Wahrnehmungsschwelle und erzeugte nur Trigger.)
  }

  setupInteractions() {
    // Button hover effects
    gsap.utils.toArray('.hero-button, .project-button').forEach(button => {
      button.addEventListener('mouseenter', () => {
        gsap.to(button, { scale: 1.05, duration: 0.3, ease: "power2.out" });
      });
      
      button.addEventListener('mouseleave', () => {
        gsap.to(button, { scale: 1, duration: 0.3, ease: "power2.out" });
      });
    });

    // Scroll indicator animation
    const scrollIcon = document.querySelector('.scroll-icon');
    if (scrollIcon) {
      gsap.fromTo(scrollIcon,
        { y: 0 }, // Start position
        {
          y: 10, // End position (only goes down)
          duration: 1.5,
          yoyo: true,
          repeat: -1,
          ease: "power2.inOut"
        }
      );
    }

    // Mobile image reordering
    this.handleMobileImageOrdering();
  }

  handleMobileImageOrdering() {
    const reorderImages = () => {
      if (window.innerWidth <= 768) {
        // Move images before action buttons on mobile
        document.querySelectorAll('.project-section').forEach(section => {
          const image = section.querySelector('.project-image');
          const content = section.querySelector('.project-content');
          const action = section.querySelector('.project-action');

          if (image && content && action && !image.dataset.moved) {
            // Insert image before action buttons within content
            content.insertBefore(image, action);
            image.dataset.moved = 'true';
          }
        });
      } else {
        // Restore original layout on desktop
        document.querySelectorAll('.project-section').forEach(section => {
          const image = section.querySelector('.project-image');
          const layout = section.querySelector('.project-layout');

          if (image && layout && image.dataset.moved) {
            // Move image back to layout container
            layout.appendChild(image);
            delete image.dataset.moved;
          }
        });
      }
    };

    // Initial reorder
    reorderImages();

    // Reorder on window resize
    window.addEventListener('resize', reorderImages);
  }

  setupSectionAlignment() {
    const sections = gsap.utils.toArray('section, footer.contact-section').filter(section => {
      // Exclude mobile-only sections on desktop
      return !section.classList.contains('mobile-stats-section');
    });
    let scrollTimeout;
    let isAligning = false;

    const alignToNearestSection = () => {
      if (isAligning || this.isNavigating) return;
      
      // Only enable on desktop and larger tablets
      if (window.innerWidth <= 1024) return;
      
      const viewportHeight = window.innerHeight;
      
      // Find the section closest to the top of the viewport
      let closestSection = null;
      let closestDistance = Infinity;
      
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top;
        
        // Only consider sections that are close to the top viewport
        // Allow sections that are a bit above or below the top of viewport
        if (sectionTop >= -300 && sectionTop < viewportHeight * 0.4) {
          const distance = Math.abs(sectionTop);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = section;
          }
        }
      });
      
      // If we found a close section and it's close to the top viewport
      // Use larger threshold for sections above viewport (like hero when scrolled down)
      const threshold = closestSection && closestSection.getBoundingClientRect().top < 0 ? 300 : 200;
      if (closestSection && closestDistance <= threshold && closestDistance > 8) {
        isAligning = true;
        const targetY = closestSection.offsetTop;
        
        gsap.to(window, {
          duration: 0.6,
          scrollTo: {
            y: targetY,
            autoKill: false
          },
          ease: "power2.out",
          onComplete: () => {
            isAligning = false;
          }
        });
      }
    };

    // Listen for scroll end (when user stops scrolling)
    const handleScroll = () => {
      if (isAligning) return;
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        alignToNearestSection();
      }, 1000); // Wait 1 second after scrolling stops
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  // Kein pauschales Refresh bei jedem Resize (mobile Adressleiste = Jank).
  // Layout-relevante Aenderungen werden gezielt behandelt: orientationchange +
  // load (siehe init) und ignoreMobileResize (siehe ScrollTrigger.config oben).
  handleResize() {}
}